<script>
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import CsatMetrics from './components/CsatMetrics.vue';
import CsatTable from './components/CsatTable.vue';
import CsatFilters from './components/Csat/CsatFilters.vue';
import { generateFileName } from '../../../../helper/downloadHelper';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

export default {
  name: 'CsatResponses',
  components: {
    Dialog,
    CsatMetrics,
    CsatTable,
    CsatFilters,
    ReportHeader,
    V4Button,
  },
  data() {
    return {
      editorStates: {},
      resolveDiscard: null,
      pageIndex: 0,
      from: 0,
      to: 0,
      userIds: [],
      inbox: null,
      team: null,
      rating: null,
      loadError: '',
      loadRequestId: 0,
      isDownloading: false,
      viewActive: true,
      reportReady: false,
      responsesReady: false,
      reportLoading: false,
      responsesLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledOnAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    hasDrafts() { return Object.values(this.editorStates).some(state => state.dirty); },
    hasPendingSave() { return Object.values(this.editorStates).some(state => state.saving); },
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        user_ids: this.userIds,
        inbox_id: this.inbox,
        team_id: this.team,
        rating: this.rating,
      };
    },
    isTeamsEnabled() {
      return this.isFeatureEnabledOnAccount(
        this.accountId,
        FEATURE_FLAGS.TEAM_MANAGEMENT
      );
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    window.addEventListener('beforeunload', this.beforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.beforeUnload);
    this.resolveDiscard?.(false);
    this.viewActive = false;
    this.loadRequestId += 1;
  },
  beforeRouteLeave() { return this.confirmDiscard(); },
  beforeRouteUpdate() { return this.confirmDiscard(); },
  methods: {
    onEditorState({ id, ...state }) { this.editorStates[id] = state; },
    beforeUnload(event) {
      if (!this.hasDrafts && !this.hasPendingSave) return;
      event.preventDefault();
      event.returnValue = '';
    },
    confirmDiscard() {
      if (this.hasPendingSave) {
        useAlert(this.$t('CSAT_REPORTS.REVIEW_NOTES.WAIT_SAVE'));
        return false;
      }
      if (!this.hasDrafts) return true;
      this.resolveDiscard?.(false);
      return new Promise(resolve => {
        this.resolveDiscard = resolve;
        this.$refs.discardDialog.open();
      });
    },
    finishDiscard(allowed) {
      const resolve = this.resolveDiscard;
      this.resolveDiscard = null;
      if (resolve) this.$refs.discardDialog.close();
      resolve?.(allowed);
    },
    async beforeReportChange() {
      if (!(await this.confirmDiscard())) return false;
      this.editorStates = {};
      return true;
    },
    async retryReport() {
      if (await this.beforeReportChange()) return this.getAllData();
    },
    async getAllData() {
      this.loadRequestId += 1;
      const requestId = this.loadRequestId;
      const accountId = this.accountId;
      this.loadError = '';
      this.reportReady = false;
      this.responsesReady = false;
      this.reportLoading = true;
      this.responsesLoading = false;
      try {
        await Promise.all([
          this.$store.dispatch('csat/getMetrics', this.requestPayload),
          this.getResponses(),
        ]);
        if (requestId !== this.loadRequestId || accountId !== this.accountId) return;
        this.reportReady = true;
        this.responsesReady = true;
      } catch {
        if (requestId !== this.loadRequestId || accountId !== this.accountId)
          return;
        this.loadError = this.$t('REPORT.DATA_FETCHING_FAILED');
        useAlert(this.loadError);
      } finally {
        if (requestId === this.loadRequestId && accountId === this.accountId) {
          this.reportLoading = false;
        }
      }
    },
    getResponses() {
      return this.$store.dispatch('csat/get', {
        page: this.pageIndex + 1,
        ...this.requestPayload,
      });
    },
    async downloadReports() {
      if (this.isDownloading) return;
      this.isDownloading = true;
      const accountId = this.accountId;
      const type = 'csat';
      try {
        await this.$store.dispatch('csat/downloadCSATReports', {
          fileName: generateFileName({ type, to: this.to }),
          ...this.requestPayload,
        });
      } catch {
        if (this.viewActive && this.accountId === accountId) {
          useAlert(this.$t('CSAT_REPORTS.DOWNLOAD_FAILED'));
        }
      } finally {
        this.isDownloading = false;
      }
    },
    async onPageNumberChange(pageIndex) {
      if (this.reportLoading || !(await this.beforeReportChange())) return;
      this.pageIndex = pageIndex;
      this.loadRequestId += 1;
      const requestId = this.loadRequestId;
      const accountId = this.accountId;
      this.loadError = '';
      this.responsesReady = false;
      this.responsesLoading = true;
      try {
        await this.getResponses();
        if (requestId !== this.loadRequestId || accountId !== this.accountId) return;
        this.responsesReady = true;
      } catch {
        if (requestId !== this.loadRequestId || accountId !== this.accountId)
          return;
        this.loadError = this.$t('REPORT.DATA_FETCHING_FAILED');
        useAlert(this.loadError);
      } finally {
        if (requestId === this.loadRequestId && accountId === this.accountId) {
          this.responsesLoading = false;
        }
      }
    },
    onFilterChange({
      from,
      to,
      selectedAgents,
      selectedInbox,
      selectedTeam,
      selectedRating,
    }) {
      // do not track filter change on initial load
      if (this.from !== 0 && this.to !== 0) {
        useTrack(REPORTS_EVENTS.FILTER_REPORT, {
          filterType: 'date',
          reportType: 'csat',
        });
      }

      this.pageIndex = 0;
      this.from = from;
      this.to = to;
      this.userIds = selectedAgents.map(el => el.id);
      this.inbox = selectedInbox?.id;
      this.team = selectedTeam?.id;
      this.rating = selectedRating?.value;

      this.getAllData();
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('CSAT_REPORTS.HEADER')">
    <V4Button
      :label="$t('CSAT_REPORTS.DOWNLOAD')"
      icon="i-ph-download-simple"
      size="sm"
      :is-loading="isDownloading"
      :disabled="isDownloading"
      @click="downloadReports"
    />
  </ReportHeader>

  <div class="flex flex-col gap-6">
    <CsatFilters
      :show-team-filter="isTeamsEnabled"
      :before-change="beforeReportChange"
      :error-message="loadError"
      @filter-change="onFilterChange"
      @retry="retryReport"
    />
    <p v-if="reportLoading" role="status" class="p-4 text-sm text-n-slate-11">{{ $t('CSAT_REPORTS.LOADING') }}</p>
    <template v-else-if="reportReady">
      <CsatMetrics :filters="requestPayload" />
      <p v-if="responsesLoading" role="status" class="p-4 text-sm text-n-slate-11">{{ $t('CSAT_REPORTS.LOADING_RESPONSES') }}</p>
      <CsatTable v-else-if="responsesReady" :page-index="pageIndex" @page-change="onPageNumberChange" @editor-state="onEditorState" />
    </template>
  </div>
  <Dialog ref="discardDialog" type="alert"
    :title="$t('CSAT_REPORTS.REVIEW_NOTES.DISCARD_TITLE')"
    :description="$t('CSAT_REPORTS.REVIEW_NOTES.DISCARD_DESCRIPTION')"
    :confirm-button-label="$t('WORKSPACE_V2.TASK_EDITOR.DISCARD')"
    :cancel-button-label="$t('WORKSPACE_V2.TASK_EDITOR.KEEP_EDITING')"
    @confirm="finishDiscard(true)" @close="finishDiscard(false)" />
</template>
